// The table. One search term per row.
//
// The PRD pulls in two directions here. It lists eighteen columns, and it also
// says fifteen columns is a spreadsheet and the default view should show few.
// Both hold: the default stays short, and the four columns that are a
// sub-feature's whole point — concentration, who wins a term, price against the
// term, and the delivery lift — can be switched on, and are switched on
// already whenever the filter in use is about them. A seller on the competitor
// wins filter who cannot see which competitor, or by how much, is reading a
// list with its subject removed.
//
// The leak is the verdict; the four shares are its evidence, which is why they
// sit beside it rather than under it.
import { Link } from 'react-router-dom';
import React, { useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import s from '../KeywordPerformance.module.css';
import { CLASS_LABEL, money, movement as fmtMovement, num, pct } from '../format';

const STAGE_TONE = {
  visibility: 'bad', attraction: 'bad', page: 'warn', checkout: 'warn', holds: 'good',
};

// The PRD columns that are off by default. Each names the sub-feature it
// belongs to, so the strip above the table reads as the document does.
export const EXTRA_COLUMNS = [
  { id: 'concentration', label: 'Top-3 hold', ref: 'SF4' },
  { id: 'holders', label: 'Who wins it', ref: 'SF5' },
  { id: 'price', label: 'Price vs term', ref: 'SF3' },
  { id: 'delivery', label: 'Delivery lift', ref: 'SF3' },
];

// Which of them a filter turns on by itself. A filter whose finding cannot be
// read without a column has no business hiding it.
export const EXTRAS_FOR_VIEW = {
  competitor_wins: ['holders', 'concentration'],
  opportunity: ['concentration'],
  // A price filter whose price column is off is a list with its subject hidden.
  price_gap: ['price'],
};

function ExtraCell({ id, row }) {
  switch (id) {
    case 'concentration':
      // Three different blanks. "Not ranked" says the reading cannot be taken
      // for this term at all, which is not the same as a window nobody has
      // collected, and neither is a low number.
      if (row.concentration != null) {
        return <td className={s.right}>{pct(row.concentration, 0)}</td>;
      }
      return (
        <td className={s.right}>
          <span className={s.unknown}>
            {row.concentration_state === 'not_in_top_terms' ? 'not ranked' : '—'}
          </span>
        </td>
      );

    case 'holders': {
      // The competitor is the finding, so they lead. With none named on this
      // term the top holder still answers "who wins it", which is the column.
      const holders = row.holders || [];
      const rival = holders.find((h) => h.competitor);
      const lead = rival || holders[0];
      if (!lead) return <td className={s.muted}>—</td>;
      return (
        <td>
          <span className={s.whoWins}>
            <span className={lead.is_seller ? s.holderYou
                   : lead.competitor ? s.holderRival : s.holderOther}>
              {lead.competitor || lead.asin}
            </span>
            <span className={s.whoFigure}>{pct(lead.click_share, 0)}</span>
            {/* Their share against yours: a 60% holder reads differently from
                a 12% one, and neither reads at all without your own beside it. */}
            <span className={s.muted}>vs you {pct(row.shares.clicks, 1)}</span>
          </span>
        </td>
      );
    }

    case 'price':
      if (!row.price) return <td className={s.muted}>—</td>;
      return (
        <td className={s.right}>
          <span className={row.price.position === 'above' ? s.down
                 : row.price.position === 'below' ? s.up : s.flat}>
            {money(row.price.own)}
          </span>
          <span className={s.subFigure}>term {money(row.price.market)}</span>
        </td>
      );

    case 'delivery':
      if (!row.delivery) return <td className={s.muted}>—</td>;
      return (
        <td className={s.right}>
          <span className={row.delivery.lift_pp > 0 ? s.up : s.down}>
            {row.delivery.lift_pp > 0 ? '+' : ''}{row.delivery.lift_pp}pp
          </span>
          {!row.delivery.rankable && <span className={s.subFigure}>too few buys</span>}
        </td>
      );

    default:
      return <td />;
  }
}

function Shares({ shares }) {
  // The funnel as four bars. Read left to right, a share that drops sharply
  // between two bars is the leak the verdict column names.
  const stages = [['impressions', 'Impr'], ['clicks', 'Clicks'],
                  ['cart_adds', 'Carts'], ['purchases', 'Buys']];
  return (
    <div className={s.funnel}>
      {stages.map(([k, label]) => (
        <div key={k} className={s.funnelStage}>
          <div className={s.funnelTrack}>
            <div className={s.funnelFill}
                 style={{ width: `${Math.min(100, (shares[k] || 0) * 100)}%` }} />
          </div>
          <span className={s.funnelLabel}>{label}</span>
          <span className={s.funnelValue}>{pct(shares[k], 1)}</span>
        </div>
      ))}
    </div>
  );
}

function Detail({ row, span, detail, trend, loading }) {
  return (
    <tr className={s.expansion}>
      <td colSpan={span}>
        {/* What this term is worth. The figure lives in Search Attribution,
            which allocates each product's actual search revenue across its
            terms — so it is read there rather than recomputed here, and the
            two surfaces cannot disagree about a number. */}
        <Link className={s.revenueLink}
              to={`/dashboard/seller-intelligence/search-attribution?term=${encodeURIComponent(row.search_term)}`}>
          What this term earns →
        </Link>
        <div className={s.expansionGrid}>
          <section>
            <h4 className={s.expansionTitle}>Funnel</h4>
            <Shares shares={row.shares} />
            <p className={s.hint}>{row.leak_detail}</p>
          </section>

          <section>
            <h4 className={s.expansionTitle}>Price and delivery</h4>
            {row.price ? (
              <p className={s.expansionLine}>
                You sell at {money(row.price.own)} where the term buys at{' '}
                {money(row.price.market)} — <strong>{row.price.position}</strong> the
                converting price.
              </p>
            ) : (
              <p className={s.hint}>
                No converting price to compare with — one side of this term has no
                purchases, so there is no price it buys at.
              </p>
            )}
            {row.delivery ? (
              <p className={s.expansionLine}>
                Fast delivery is {pct(row.delivery.fast_share_of_purchases, 1)} of this
                term&apos;s purchases against {pct(row.delivery.fast_share_of_clicks, 1)} of
                its clicks — {row.delivery.lift_pp > 0 ? 'a lift of ' : ''}
                {row.delivery.lift_pp}pp.
                {!row.delivery.rankable && (
                  <span className={s.muted}> Too few purchases behind it to rank on.</span>
                )}
              </p>
            ) : (
              <p className={s.hint}>Amazon publishes no delivery split for this term.</p>
            )}
          </section>

          <section>
            <h4 className={s.expansionTitle}>Who wins it</h4>
            {row.holders?.length ? (
              <>
                <ul className={s.holders}>
                  {row.holders.map((h) => (
                    <li key={h.asin}>
                      <span className={h.is_seller ? s.holderYou
                             : h.competitor ? s.holderRival : s.holderOther}>
                        {h.asin}
                      </span>
                      {h.is_seller && <span className={s.tagYou}>you</span>}
                      {h.competitor && <span className={s.tagRival}>{h.competitor}</span>}
                      <span className={s.cardFigure}>{pct(h.click_share, 1)}</span>
                    </li>
                  ))}
                </ul>
                <p className={s.hint}>
                  Top three hold {pct(row.concentration, 0)} of this term&apos;s clicks.
                  Low competition by clicks — not a bid-cost estimate.
                </p>
              </>
            ) : (
              <p className={s.hint}>
                Who holds a term comes from the marketplace Top Search Terms report, which
                has not been collected for this window.
              </p>
            )}
          </section>

          <section>
            <h4 className={s.expansionTitle}>Your products on this term</h4>
            {loading ? <p className={s.hint}>Loading…</p>
              : detail?.products?.length ? (
                <table className={s.subTable}>
                  <thead>
                    <tr>
                      <th>ASIN</th><th>Impr</th><th>Clicks</th><th>Buys</th>
                      <th>Of your clicks</th><th>Its rank</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.products.map((p) => (
                      <tr key={p.asin}>
                        <td>{p.asin}</td>
                        <td>{num(p.impressions)}</td>
                        <td>{num(p.clicks)}</td>
                        <td>{num(p.purchases)}</td>
                        <td>{pct(p.share_of_our_clicks, 0)}</td>
                        {/* Amazon scores a term per ASIN, so this is where the term
                            sits among that one product's terms — not a marketplace
                            position. */}
                        <td>{p.term_rank == null ? '—' : `#${p.term_rank}`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className={s.hint}>No per-product split for this term.</p>}
            {(() => {
              // Only windows that were collected carry a volume. An uncollected
              // window is a hole in the series, not a zero, so it is skipped
              // rather than drawn at the floor.
              const points = (trend?.points || []).filter((p) => p.collected);
              if (points.length < 2) return null;
              return (
                <p className={s.expansionLine}>
                  Volume across {points.length} collected windows:{' '}
                  {points.map((p) => num(p.volume)).join(' → ')}
                  {trend.summary?.change != null && (
                    <span className={trend.summary.change >= 0 ? s.up : s.down}>
                      {' '}{trend.summary.change >= 0 ? '▲' : '▼'}{' '}
                      {Math.abs(trend.summary.change * 100).toFixed(1)}%
                    </span>
                  )}
                </p>
              );
            })()}
          </section>
        </div>
      </td>
    </tr>
  );
}

export default function KeywordTable({ rows, showScore, extras = [], onExpand, expanded,
                                       detail, detailLoading, onCorrect }) {
  const [open, setOpen] = useState(null);
  const shown = EXTRA_COLUMNS.filter((c) => extras.includes(c.id));
  // thin + rank + keyword + volume + four shares + buys + leak, then the score
  // and whatever extras are on. The expansion spans all of them.
  const span = 10 + (showScore ? 1 : 0) + shown.length;

  const toggle = (term) => {
    const next = open === term ? null : term;
    setOpen(next);
    if (next) onExpand(next);
  };

  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <thead>
          <tr>
            <th className={s.thin} />
            <th>Rank</th>
            <th>Keyword</th>
            <th className={s.right}>Volume</th>
            <th className={s.right}>Impr share</th>
            <th className={s.right}>Click share</th>
            <th className={s.right}>Cart share</th>
            <th className={s.right}>Buy share</th>
            <th className={s.right}>Buys</th>
            <th>Where you lose it</th>
            {shown.map((c) => (
              <th key={c.id} className={c.id === 'holders' ? undefined : s.right}>
                {c.label}<span className={s.thRef}>{c.ref}</span>
              </th>
            ))}
            {showScore && <th className={s.right}>Score</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const move = fmtMovement(row.movement);
            const isOpen = open === row.search_term;
            return (
              <React.Fragment key={row.search_term}>
                <tr className={isOpen ? s.rowOpen : undefined}>
                  <td className={s.thin}>
                    <button className={s.expandButton} onClick={() => toggle(row.search_term)}
                            aria-label={isOpen ? 'Collapse' : 'Expand'}>
                      {isOpen ? <FiChevronDown /> : <FiChevronRight />}
                    </button>
                  </td>
                  <td>
                    <span className={s.rank}>{row.rank}</span>
                    <span className={s[move.tone]}>{move.text}</span>
                  </td>
                  <td>
                    <span className={s.term}>{row.search_term}</span>
                    {row.classification !== 'generic' && (
                      <button className={s.classTag}
                              title={row.matched_brand
                                ? `Matched "${row.matched_brand}". Click to correct.`
                                : 'Click to correct.'}
                              onClick={() => onCorrect(row.search_term,
                                row.classification === 'branded' ? 'generic' : 'branded')}>
                        {CLASS_LABEL[row.classification]}
                      </button>
                    )}
                  </td>
                  <td className={s.right}>{num(row.volume)}</td>
                  <td className={s.right}>{pct(row.shares.impressions, 2)}</td>
                  <td className={s.right}>{pct(row.shares.clicks, 2)}</td>
                  <td className={s.right}>{pct(row.shares.cart_adds, 2)}</td>
                  <td className={s.right}>{pct(row.shares.purchases, 2)}</td>
                  <td className={s.right}>{num(row.purchases)}</td>
                  <td>
                    <span className={`${s.leak} ${s[STAGE_TONE[row.leak] || 'neutral']}`}>
                      {row.leak_label}
                    </span>
                  </td>
                  {shown.map((c) => <ExtraCell key={c.id} id={c.id} row={row} />)}
                  {showScore && (
                    <td className={s.right}>
                      {row.effectiveness == null ? '—' : Math.round(row.effectiveness)}
                    </td>
                  )}
                </tr>
                {isOpen && (
                  <Detail row={row} span={span}
                          detail={expanded === row.search_term ? detail : null}
                          trend={expanded === row.search_term ? detail?.trend : null}
                          loading={detailLoading} />
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
